import React, { useCallback, useMemo } from 'react'
import {
  Button,
  BUTTON_SIZE,
  BUTTON_TYPE,
  DataType,
  InformationPanel,
  ReceiptCard,
  styled,
} from '~/app/components'
import { InformationFormat } from '~/app/components/order-list/details/InformationPanel'
import { isoToFormat } from '~/app/utils/useIsoToFormat'
import { usePgReceiptInfoLazyQuery } from '~/__generated__/app/query/pgReceiptInfo/pgReceiptInfo.graphql'
import { useLoading } from '~/app/components/common/LoadingProvider'
import { getYaNative } from '@yanolja-org/ya-native'
import { toKRW } from '~/app/utils/krNumberToString'
import { isNative } from '~/app/utils/typeguards'
import { ApolloError } from '@apollo/client'

const ERROR_MESSAGE = '영수증을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'

export type ReceiptInformation = {
  id: string
  createdAt: string
  title: string
  price: number
}
type ReceiptProps = {
  onFetchPgReceiptError: (error: ApolloError) => void
  information: ReceiptInformation
  hasPGPayment: boolean
}

const ButtonContainer = styled.div`
  display: flex;
  margin: 1.2rem 1rem 0;
  padding: 0 1rem;
  justify-content: space-between;

  & button {
    margin-right: 0.8rem;
    :nth-last-of-type {
      margin-right: 0;
    }
  }
`

const ReceiptItem: React.FC<ReceiptProps> = function ({
  information,
  hasPGPayment,
  onFetchPgReceiptError,
}) {
  const [getPgReceiptInfo, { loading }] = usePgReceiptInfoLazyQuery()

  useLoading(loading)

  const onButtonClick = useCallback(async () => {
    if (!hasPGPayment) return

    const { data, error } = await getPgReceiptInfo({
      variables: {
        tenancyCode: 'ORDER',
        orderId: information.id,
      },
    })

    if (error) {
      onFetchPgReceiptError(error)
      return
    }

    const receiptUrl = data?.pgReceiptInfo?.receiptUrl
    if (!receiptUrl) {
      onFetchPgReceiptError(new ApolloError({ errorMessage: ERROR_MESSAGE }))
      return
    }

    const isCashReceipt = receiptUrl.includes('nol-money.yanolja.com/cash-receipt')

    const yaNative = getYaNative()
    if (isNative(yaNative)) {
      yaNative.wv({
        method: 'get',
        url: receiptUrl,
        ...(!isCashReceipt && {
          layout: {
            title: '결제영수증',
            modal: false,
          },
        }),
      })
    } else {
      window.open(receiptUrl, '_blank')
    }
  }, [getPgReceiptInfo, hasPGPayment, information.id, onFetchPgReceiptError])

  const receiptInformation = useMemo<Array<InformationFormat>>(() => {
    const { id, title, price, createdAt } = information
    const date = isoToFormat(createdAt, 'yyyy.MM.dd HH:mm')
    return [
      { type: DataType.TitleDescriptionSmall, title: '통합 주문번호', description: id },
      { type: DataType.TitleDescriptionSmall, title: '거래일시', description: date },
      { type: DataType.TitleDescriptionSmall, title: '상품명', description: title },
      { type: DataType.Dash },
      {
        type: DataType.EmphasizedDescription,
        title: '결제금액',
        description: toKRW(price),
      },
      { type: DataType.TitleDescriptionSmall, title: '회사명', description: '놀유니버스' },
    ]
  }, [information])

  return (
    <div>
      <ReceiptCard style={{ paddingTop: '1.2rem' }}>
        <InformationPanel information={receiptInformation} />
      </ReceiptCard>
      {hasPGPayment && (
        <ButtonContainer>
          <Button
            title={'결제영수증 보기'}
            type={BUTTON_TYPE.BASIC}
            size={BUTTON_SIZE.M}
            onClick={onButtonClick}
            isFullSize
          />
        </ButtonContainer>
      )}
    </div>
  )
}

export default ReceiptItem
